import React from "react";
import { Spin, Tag } from "antd";
import { Warning, Watch } from "assets/icons";
import { ISummaryData } from "store/attendance/attendance-model";
import theme from "styles/theme";
import useTranslation from "utils/hooks/useTranslation";
import { checkinDataInformations, releasedInformations, inboundDataInformations } from "./dataInformations";
import Informations from "./Informations";
import * as S from "./styled/index.styled";

export type ISummary = {
  activeSummaryData: ISummaryData;
  isLoadingSummary: boolean;
  isSidebarCollapsed?: boolean;
  children?: React.ReactNode;
};

const Summary = ({ activeSummaryData, isLoadingSummary, isSidebarCollapsed = true, children }: ISummary): JSX.Element => {
  const VEHICLES_WITH_CHECKIN = useTranslation({ id: "vehicles_with_checkin" });
  const VEHICLES_WITHOUT_CHECKIN = useTranslation({ id: "vehicles_without_checkin" });
  const VEHICLES_IN_LOCATION = useTranslation({ id: "vehicles_in_location" });
  const AVERAGE_TIME = useTranslation({ id: "average_time" });
  const NOT_RELEASED = useTranslation({ id: "not_released" });
  const RELEASED = useTranslation({ id: "released" });
  const INTERN_QUEUE = useTranslation({ id: "intern_queue" });
  const IN_OPERATION = useTranslation({ id: "in_operation" });
  const FINISHED_OPERATION = useTranslation({ id: "finished_operation" });
  const REQUIRES_ATTENTION = useTranslation({ id: "requires_attention" });
  const VEHICLES_IN_THE_RADIUS = useTranslation({ id: "vehicles_in_the_radius" });
  const VEHICLES_ARRIVING = useTranslation({ id: "vehicles_arriving" });

  const timeConverter = (time) => {
    if (time === null) {
      return "--:--";
    }
    const day = parseInt(time, 10);
    const hour = +time.split(":")[0].split(".")[1];
    const minutes = +time.slice(-2);
    const timeConvert = `${day.toString().padStart(2, "0")}d ${hour.toString().padStart(2, "0")}h ${minutes.toString().padStart(2, "0")}m`;
    return timeConvert;
  };

  return (
    <S.SummaryContainer>
      <S.SummaryCards $isSidebarCollapsed={isSidebarCollapsed}>
        <S.CheckinSummaryLayout>
          <div style={isLoadingSummary === true ? S.LoadingContainer : null}>
            {isLoadingSummary ? (
              <Spin />
            ) : (
              <S.CheckinSummaryContainer>
                <div>
                  <div>
                    <S.CheckinSummaryTitle>{VEHICLES_ARRIVING}</S.CheckinSummaryTitle>
                    <S.NumberDescription>{activeSummaryData.presented}</S.NumberDescription>
                  </div>
                </div>
                <div className="divider" />
                <div>
                  <S.SummaryExtra>
                    <div>
                      <S.CheckinSummaryTitle>{VEHICLES_IN_THE_RADIUS}</S.CheckinSummaryTitle>
                      <S.CheckinInfoContainer>
                        <S.NumberDescription>{activeSummaryData.inRadiusWithCheckin}</S.NumberDescription>
                        <S.SummaryDescription>{VEHICLES_WITH_CHECKIN.split(" ").slice(1).join(" ")}</S.SummaryDescription>
                      </S.CheckinInfoContainer>
                    </div>
                    <div>
                      <Informations informations={checkinDataInformations()} />
                    </div>
                  </S.SummaryExtra>
                  <S.CheckinInfoContainer>
                    <S.NumberDescription>{activeSummaryData.inRadiusWithoutCheckin}</S.NumberDescription>
                    <S.SummaryDescription>{VEHICLES_WITHOUT_CHECKIN.split(" ").slice(1).join(" ")}</S.SummaryDescription>
                  </S.CheckinInfoContainer>
                </div>
              </S.CheckinSummaryContainer>
            )}
          </div>
        </S.CheckinSummaryLayout>
        <S.LocationSummaryLayout>
          <div style={isLoadingSummary === true ? S.LoadingContainer : null}>
            {isLoadingSummary ? (
              <Spin />
            ) : (
              <>
                <S.SummaryExtra>
                  <div>
                    <S.SummaryDescription>{NOT_RELEASED}</S.SummaryDescription>
                    <S.NumberDescription>{activeSummaryData.notReleased}</S.NumberDescription>
                  </div>
                  <div>
                    <Informations informations={releasedInformations()} />
                  </div>
                </S.SummaryExtra>
                <div>
                  <S.InboundSummarySubtitle>{RELEASED}</S.InboundSummarySubtitle>
                  <S.NumberDescription>{activeSummaryData.released}</S.NumberDescription>
                </div>
              </>
            )}
          </div>
        </S.LocationSummaryLayout>
        <S.InboundSummaryLayout>
          <div style={isLoadingSummary === true ? S.LoadingContainer : null}>
            {isLoadingSummary ? (
              <Spin />
            ) : (
              <S.InboundSummaryContainer>
                <div>
                  <div>
                    <S.InboundSummaryDescription>{VEHICLES_IN_LOCATION}</S.InboundSummaryDescription>
                    <S.NumberDescription>{activeSummaryData.actives}</S.NumberDescription>
                  </div>
                  <div>
                    <S.SummaryDescription>{AVERAGE_TIME}</S.SummaryDescription>
                    <S.InboundSummaryTime>
                      <Watch />
                      {`${"\xa0"}${timeConverter(activeSummaryData.averageTimeActives)}`}
                    </S.InboundSummaryTime>
                  </div>
                </div>
                <div className="divider" />
                <div>
                  <div>
                    <S.InboundSummarySubtitle>{INTERN_QUEUE}</S.InboundSummarySubtitle>
                    <S.NumberDescription>{activeSummaryData.waitingInOperation}</S.NumberDescription>
                  </div>
                  <div>
                    <S.SummaryDescription>{IN_OPERATION}</S.SummaryDescription>
                    <S.NumberDescription>{activeSummaryData.inOperation}</S.NumberDescription>
                  </div>
                </div>
                <div className="divider" />
                <div>
                  <S.SummaryExtra>
                    <div>
                      <S.SummaryDescription>{FINISHED_OPERATION}</S.SummaryDescription>
                      <S.NumberDescription>{activeSummaryData.finishOperation}</S.NumberDescription>
                    </div>
                    <div>
                      <Informations informations={inboundDataInformations()} />
                    </div>
                  </S.SummaryExtra>
                  {activeSummaryData.warningFinishOperation > 0 && (
                    <div>
                      <Tag className="warningIcon" style={{ color: theme["@color-warning-600"] }} icon={<Warning />} color={theme["@color-warning-100"]}>
                        {`${activeSummaryData.warningFinishOperation} ${REQUIRES_ATTENTION}`}
                      </Tag>
                    </div>
                  )}
                </div>
              </S.InboundSummaryContainer>
            )}
          </div>
        </S.InboundSummaryLayout>
      </S.SummaryCards>
      {children && <S.SummaryActions $isSidebarCollapsed={isSidebarCollapsed}>{children}</S.SummaryActions>}
    </S.SummaryContainer>
  );
};

export default Summary;
